import {ChangeDetectorRef, Component, inject, Input, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {AbstractControl, FormBuilder, ValidatorFn, Validators} from "@angular/forms";
import {Router} from "@angular/router";
import {Store} from "@ngrx/store";
import {map, Subscription} from "rxjs";
import {FundValuation} from "../../../fund/models/fund-valuation";
import {DecimalFormatPipe} from "../../../shared/pipes/decimal-format/decimal-format.pipe";
import {EMPTY_MONEY, TraceableMoney} from "../../../shared/model/traceable";
import {EMPTY_SELLER_POSITION, SellerPosition} from "../../models/seller-position";
import {DealFundsManagementActions, SellerPositionActions} from "../../store/deal.actions";
import {selectSellerPositionsWithFundNames} from "../../store/seller-position/seller-position.selectors";
import {selectDealFundsManagementFundValuations, selectSelectedDealClosingDate} from "../../store/deal/deal.selectors";
import {DateUtil} from "../../../shared/utils/date-util";
import {MatStepper} from "@angular/material/stepper";
import {numberNotBiggerThan190000m} from "../../../shared/utils/form-validators";

@Component({
    selector: "valumize-deal-funds-manage-dialog",
    templateUrl: "./deal-funds-manage-dialog.component.html",
    styleUrls: ["./deal-funds-manage-dialog.component.scss"],
    standalone: false
})
export class DealFundsManageDialogComponent implements OnInit, OnDestroy {
    private readonly store = inject(Store);
    private readonly router = inject(Router);
    private readonly formBuilder = inject(FormBuilder);
    private readonly cdr = inject(ChangeDetectorRef);

    @Input() dealId?: number;
    @ViewChild("stepper") stepper!: MatStepper;

    fundValuationColumns = ["fundName", "description", "closingDate"];
    dealFundsColumns = ["fundName", "fundValuationDescription", "closingDate", "sellerCommitment", "remove"];

    subscriptions: Subscription[] = [];
    dealFundsManagementFundValuations$ = this.store.select(selectDealFundsManagementFundValuations);

    dealClosingDate?: string;
    selectedFundName?: string;
    selectedFundId?: number;
    selectedFundSize?: TraceableMoney;
    selectedFundValuation?: FundValuation;
    sellerPositionsForDeal: SellerPosition[] = [];

    sellerPositionForm = this.formBuilder.group({
        sellerCommitment: this.formBuilder.control<number | null>(null, {nonNullable: true})
    });

    constructor() {
        this.store.dispatch(DealFundsManagementActions.loadFunds());
    }

    ngOnInit() {
        if (this.dealId) {
            this.store.dispatch(SellerPositionActions.loadAll({dealId: this.dealId}));
        }

        this.subscriptions.push(this.store.select(selectSellerPositionsWithFundNames).pipe(map(sellerPositionsWithFundNames =>
            this.sellerPositionsForDeal = sellerPositionsWithFundNames
        )).subscribe());

        this.subscriptions.push(this.store.select(selectSelectedDealClosingDate).pipe(map((closingDate) =>
            this.dealClosingDate = closingDate.date
        )).subscribe());
    }

    onStepChange(index: number) {
        const control = this.sellerPositionForm.controls.sellerCommitment;

        if (index === 2) { // Step 3 (0-based index)
            control.setValidators([
                Validators.required,
                numberNotBiggerThan190000m(),
                this.sellerCommitmentNotGreaterThanFundSizeValidator()
            ]);
        } else {
            control.clearValidators();
        }

        control.updateValueAndValidity();
    }

    save() {
        if (!this.dealId) {
            return;
        }
        let sellerPositionsToSave: SellerPosition[] = [];
        this.sellerPositionsForDeal.forEach(sellerPosition => {
            sellerPositionsToSave = [...sellerPositionsToSave, sellerPosition];
        });

        this.store.dispatch(SellerPositionActions.saveAll({dealId: this.dealId, sellerPositions: sellerPositionsToSave}));
    }

    selectFundAndLoadValuations(fundId: number, fundName: string, fundSize: TraceableMoney) {
        this.selectedFundName = fundName;
        this.selectedFundId = fundId;
        this.selectedFundSize = fundSize;
        this.store.dispatch(DealFundsManagementActions.loadFundValuations({fundId}));

        this.cdr.detectChanges();
        this.stepper.next();
    }

    navigateToFund() {
        if (this.selectedFundId) {
            this.router.navigate(["/funds", this.selectedFundId]);
        }
    }

    selectFundValuation(fundValuation: FundValuation) {
        this.selectedFundValuation = fundValuation;
        this.cdr.detectChanges();
        this.stepper.next();
    }

    addFundValuationToDeal() {
        if (!this.selectedFundValuation?.id) {
            return;
        }
        const sellerPosition = {
            ...EMPTY_SELLER_POSITION,
            fundValuationId: this.selectedFundValuation.id,
            fundName: this.selectedFundName,
            fundValuationDescription: this.selectedFundValuation.description.text,
            closingDate: this.selectedFundValuation.closingDate,
            sellerCommitment: {
                ...EMPTY_MONEY,
                amount: DecimalFormatPipe.transformToMillionsNum(this.sellerPositionForm.getRawValue().sellerCommitment ?? undefined)
            }
        };
        this.sellerPositionsForDeal = [...this.sellerPositionsForDeal, sellerPosition];
        this.selectedFundName = undefined;
        this.selectedFundValuation = undefined;
    }

    removeFundValuationFromDeal(sellerPositionToRemove: SellerPosition) {
        this.sellerPositionsForDeal = this.sellerPositionsForDeal.filter((sellerPosition) => sellerPosition !== sellerPositionToRemove);
    }

    filterFundValuations(fundValuations: FundValuation[]): FundValuation[] {
        const dealClosingDate = DateUtil.fromIsoDate(this.dealClosingDate);

        return fundValuations.filter(fundValuation => {
            const alreadyAdded = this.sellerPositionsForDeal.some(sellerPosition => sellerPosition.fundValuationId === (fundValuation.id ?? -1));
            const fundValuationClosingDate = DateUtil.fromIsoDate(fundValuation.closingDate.date);

            return !!fundValuationClosingDate && !!dealClosingDate && dealClosingDate <= fundValuationClosingDate && !alreadyAdded;
        });
    }

    goBackToList(): void {
        this.selectedFundName = undefined;
        this.selectedFundValuation = undefined;
        this.selectedFundId = undefined;
        this.stepper.previous();
    }

    goBackToValuations(): void {
        this.selectedFundValuation = undefined;
        this.stepper.previous();
    }

    resetStepper() {
        this.selectedFundName = undefined;
        this.selectedFundValuation = undefined;
        this.stepper.reset();
    }

    sellerCommitmentNotGreaterThanFundSizeValidator(): ValidatorFn {
        return (control: AbstractControl) => {
            const rawValue = control.value;

            if (rawValue == null || this.selectedFundSize == null) {
                return null;
            }

            const commitmentAmount = DecimalFormatPipe.transformToMillionsNum(rawValue);
            const fundSizeAmount = this.selectedFundSize.amount;

            if (commitmentAmount && fundSizeAmount && commitmentAmount > fundSizeAmount) {
                return {sellerCommitmentTooHigh: true};
            }

            return null;
        };
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
}
